// openaiService.js
import { getSystemPrompt } from './systemPrompts';

const DEFAULT_MODEL = 'gpt-4o-mini';
const DEFAULT_TIMEOUT_MS = 60000;

function getBaseUrl() {
  // 1) Prefer explicit build-time envs (Netlify/Vite/CRA)
  const envUrl =
    process.env.REACT_APP_API_URL ||
    process.env.VITE_API_BASE_URL ||
    process.env.VITE_API_BASE_URL_RUNTIME || // optional runtime override
    process.env.REACT_APP_API_BASE_URL_RUNTIME || // optional runtime override
    '';

  if (envUrl) return envUrl;

  // 2) Runtime fallback: if running on your production domain, use Render backend
  try {
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
      const host = window.location.hostname;
      if (host.includes('figureitoutnow.in') || host.includes('figureitoutnow.onrender.com')) {
        return 'https://figureitoutnow.onrender.com';
      }
    }
  } catch (e) {
    // ignore runtime detection errors
  }

  // 3) Default: relative path (useful for local dev where CRA/Vite proxies '/openai')
  return '';
}

function getModelName() {
  return (
    process.env.VITE_OPENAI_MODEL ||
    process.env.REACT_APP_OPENAI_MODEL ||
    DEFAULT_MODEL
  );
}

/**
 * Calls your backend proxy at /openai (relative) or `${baseUrl}/openai`.
 * Returns a plain string (assistant content) or a friendly error message string.
 */
export const callOpenAI = async ({ messages = [], screenContext = null } = {}) => {
  const fullMessages = [getSystemPrompt(screenContext), ...messages];

  const baseUrl = getBaseUrl(); // '' means relative path -> '/openai'
  const model = getModelName();

  console.info('🧠 Sending to backend proxy with model:', model);
  console.debug('🧠 Full messages:', fullMessages);

  // build endpoint: if baseUrl is empty -> '/openai', else ensure no trailing slash then append '/openai'
  const endpoint = baseUrl
    ? `${baseUrl.replace(/\/$/, '')}/openai`
    : '/openai';

  // log endpoint so you can verify in browser console (helps diagnose Netlify/Render issues)
  console.info('🛰️ Calling backend endpoint:', endpoint);

  // timeout handling via AbortController
  const controller = new AbortController();
  const timeoutMs = Number(process.env.OPENAI_REQUEST_TIMEOUT_MS) || DEFAULT_TIMEOUT_MS;
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      signal: controller.signal,
      body: JSON.stringify({
        model,
        messages: fullMessages,
        temperature: 0.85,
      }),
    });

    clearTimeout(timeout);

    // Helpful debug logging on non-OK responses
    if (!response.ok) {
      let textBody = '';
      try {
        textBody = await response.text();
      } catch (e) {
        // ignore
      }
      console.error('❌ Backend /openai request failed:', response.status, textBody);
      // Provide a clearer message depending on status
      if (response.status === 401 || response.status === 403) {
        return '⚠️ Kumar cannot access the AI service (authorization error). Check server API key.';
      }
      if (response.status >= 500) {
        return '⚠️ Kumar’s server had an issue. Please try again shortly.';
      }
      return '⚠️ Kumar couldn’t reach the server. Please check your internet or try again shortly.';
    }

    // parse JSON safely
    let data;
    try {
      data = await response.json();
    } catch (err) {
      console.error('❌ /openai returned non-JSON response:', err);
      return '⚠️ Kumar ran into a problem while thinking. Please try again.';
    }

    // If proxy returned an error shape { error: ... }
    if (data?.error) {
      console.error('❌ Proxy error payload:', data.error);
      return '⚠️ Kumar’s server returned an error. Check server logs.';
    }

    // Typical OpenAI v1 chat/completions shape:
    // data.choices => array of { message: { role, content } }
    if (Array.isArray(data?.choices) && data.choices.length > 0) {
      // join all returned choice messages into a single string (defensive)
      const joined = data.choices
        .map((c) => {
          // new (ChatCompletions) shape: c.message.content
          if (c?.message?.content) return c.message.content;
          // legacy shape or alternate: c.text
          if (typeof c?.text === 'string') return c.text;
          // some proxies return { content: "..." } inside choice
          if (typeof c?.content === 'string') return c.content;
          return '';
        })
        .filter(Boolean)
        .join('\n\n');

      if (joined.length > 0) return joined;
    }

    // Proxy might return a simplified object
    if (typeof data?.result === 'string' && data.result.length > 0) {
      return data.result;
    }
    if (typeof data?.content === 'string' && data.content.length > 0) {
      return data.content;
    }

    console.error('⚠️ Unexpected proxy response shape:', data);
    return '⚠️ Kumar ran into a problem while thinking. Please try again.';
  } catch (error) {
    clearTimeout(timeout);
    if (error.name === 'AbortError') {
      console.error('❌ OpenAI request aborted due to timeout');
      return '⚠️ Request timed out. Kumar is taking longer than expected — try again.';
    }

    // network / CORS / fetch-level errors
    console.error('❌ OpenAI API Error:', error);
    return '⚠️ Kumar couldn’t reach the server. Please check your internet or try again shortly.';
  }
};
